import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BookOpen, Building2, Check, ChevronLeft, ChevronRight, Edit3, ExternalLink,
  LayoutGrid, List, Mail, MapPin, Palette, Phone, Plus, UploadCloud, X,
} from 'lucide-react';

import {
  createCompany, loadCompanyCatalogue, loadCompanyDetail,
  prepareCompanyLogoUpload, putCompanyLogo, updateCompany,
} from './api';
import { useXomicsAuth } from './AuthContext';
import { tx } from './i18n';
import { statusLabel } from './labels';
import {
  Empty, Failed, Head, Loading, Pill, SearchField, SummaryBar, SummaryItem,
  WorkspaceTabs, XomicsSelect,
} from './parts';
import { errorMessage } from './ui';
import { useLiveResource } from './useLive';

const MAX_COMPANY_LOGO_BYTES = 2 * 1024 * 1024;
const COMPANY_LOGO_TYPE = 'image/svg+xml';

function initials(name) {
  return String(name ?? '')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0] ?? '')
    .join('')
    .toUpperCase() || '?';
}

function CompanyDirectory({ companies, viewMode, onOpen }) {
  if (companies.length === 0) {
    return (
      <Empty
        title={tx('ui.staff.nothing_matches_17e7aebf')}
        detail={tx('ui.staff.try_a_different_search_or_filter_4e653834')}
      />
    );
  }
  return (
    <div className={`xo-company-grid${viewMode === 'list' ? ' is-list' : ''}`}>
      {companies.map((company) => (
        <button type="button" key={company.id} className="xo-company-card"
          onClick={() => onOpen(company.id)}>
          <span className="xo-company-mark" aria-hidden="true">{initials(company.name)}</span>
          <div>
            <strong>{company.name}</strong>
            <span className="xo-mono">{company.code}</span>
          </div>
          <Pill tone="ok">{statusLabel('active')}</Pill>
          <ExternalLink className="xo-company-open-icon" aria-hidden="true" />
        </button>
      ))}
    </div>
  );
}

function CompanyLogoArtwork({ logoUrl, name }) {
  const tones = [
    { code: 'light', imageTone: 'navy', label: tx('ui.companies.preview_light_mode'), hex: '#FFFFFF' },
    { code: 'soft-light', imageTone: 'blue', label: tx('ui.companies.preview_soft_light'), hex: '#F4F6FB' },
    { code: 'dark', imageTone: 'light', label: tx('ui.companies.preview_dark_mode'), hex: '#10214B' },
    { code: 'slate', imageTone: 'light', label: tx('ui.companies.preview_deep_slate'), hex: '#202B46' },
    { code: 'blue', imageTone: 'blue', label: tx('ui.companies.preview_phenome_blue'), hex: '#E9EEF9' },
    { code: 'red', imageTone: 'red', label: tx('ui.companies.preview_signal_red'), hex: '#FFF0ED' },
    { code: 'green', imageTone: 'green', label: tx('ui.companies.preview_clinical_green'), hex: '#EDF6E8' },
    { code: 'gold', imageTone: 'gold', label: tx('ui.companies.preview_warm_gold'), hex: '#FFF7E7' },
  ];
  const [activeIndex, setActiveIndex] = useState(2);
  const activeTone = tones[activeIndex];

  function move(step) {
    setActiveIndex((current) => (current + step + tones.length) % tones.length);
  }

  return (
    <aside className="xo-company-brand-panel">
      <header><Palette aria-hidden="true" /><span>{tx('ui.companies.visual_identity')}</span></header>
      <div className="xo-company-logo-focus-head">
        <span><strong>{activeTone.label}</strong><small>{activeTone.hex}</small></span>
        <div>
          <button type="button" onClick={() => move(-1)}
            aria-label={tx('ui.projects.previous_50f94286')}><ChevronLeft aria-hidden="true" /></button>
          <button type="button" onClick={() => move(1)}
            aria-label={tx('ui.projects.next_bc981983')}><ChevronRight aria-hidden="true" /></button>
        </div>
      </div>
      <div className={`xo-company-logo-stage is-${activeTone.code}`}>
        <span className="xo-company-logo-viewport">
          {logoUrl
            ? <img className={`xo-company-logo-image tone-${activeTone.imageTone}`} src={logoUrl} alt={`${name} logo`} />
            : <b>{initials(name)}</b>}
        </span>
      </div>
      <div className="xo-company-logo-tones" aria-label={tx('ui.companies.logo_colour_previews')}>
        {tones.map((tone, index) => (
          <button type="button" className={`is-${tone.code}${index === activeIndex ? ' is-selected' : ''}`}
            key={tone.code} aria-pressed={index === activeIndex} onClick={() => setActiveIndex(index)}>
            <i>
              {logoUrl
                ? <img className={`xo-company-logo-image tone-${tone.imageTone}`} src={logoUrl} alt="" />
                : <b>{initials(name)}</b>}
            </i>
            <small><strong>{tone.label}</strong><b>{tone.hex}</b></small>
          </button>
        ))}
      </div>
      <p>{tx('ui.companies.logo_source_unchanged')}</p>
    </aside>
  );
}

function CompanyDetail({ company, mayManage, onClose, onEdit }) {
  return (
    <div className="xo-company-modal-backdrop" role="presentation" onMouseDown={onClose}>
      <section className="xo-company-modal" role="dialog" aria-modal="true"
        aria-labelledby="xo-company-detail-title" onMouseDown={(event) => event.stopPropagation()}>
        <header>
          <span className="xo-company-modal-symbol"><Building2 aria-hidden="true" /></span>
          <div>
            <p className="xo-eyebrow">{tx('ui.reports.company_7a199499')}</p>
            <h2 id="xo-company-detail-title">{company.name}</h2>
            <span className="xo-mono">{company.code}</span>
          </div>
          <Pill tone={company.status === 'active' ? 'ok' : 'flat'}>{statusLabel(company.status)}</Pill>
          {mayManage && (
            <button type="button" className="xo-btn-2" onClick={() => onEdit(company)}>
              <Edit3 aria-hidden="true" />{tx('ui.reports.edit_details_4173a96a')}
            </button>
          )}
          <button type="button" className="xo-icon-btn" aria-label={tx('ui.parts.close')} onClick={onClose}>
            <X aria-hidden="true" />
          </button>
        </header>

        <div className="xo-company-modal-layout">
          <div className="xo-company-modal-body">
            <div className="xo-company-contact-row">
              <MapPin aria-hidden="true" />
              <span><small>{tx('ui.cluster.fact_address')}</small><strong>{company.address}</strong></span>
            </div>
            <a className="xo-company-contact-row" href={`mailto:${company.email}`}>
              <Mail aria-hidden="true" />
              <span><small>{tx('ui.notifications.email_84add5b2')}</small><strong>{company.email}</strong></span>
            </a>
            <a className="xo-company-contact-row" href={`tel:${company.phone}`}>
              <Phone aria-hidden="true" />
              <span><small>{tx('ui.profile.phone_number_8961d3bf')}</small><strong>{company.phone}</strong></span>
            </a>
            <a className="xo-company-website" href={company.websiteUrl} target="_blank" rel="noreferrer">
              <span>{company.websiteUrl}</span><ExternalLink aria-hidden="true" />
            </a>
          </div>
          <CompanyLogoArtwork logoUrl={company.logo.url} name={company.name} />
        </div>
      </section>
    </div>
  );
}

async function sha256File(file) {
  const digest = await window.crypto.subtle.digest('SHA-256', await file.arrayBuffer());
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
}

function CompanyEditor({ company = null, onClose, onSaved }) {
  const editing = company !== null;
  const [form, setForm] = useState({
    code: company?.code ?? '',
    name: company?.name ?? '',
    websiteUrl: company?.websiteUrl ?? '',
    address: company?.address ?? '',
    email: company?.email ?? '',
    phone: company?.phone ?? '',
  });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(company?.logo?.url ?? null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (logoFile === null) {
      setLogoPreview(company?.logo?.url ?? null);
      return undefined;
    }
    const url = URL.createObjectURL(logoFile);
    setLogoPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [company?.logo?.url, logoFile]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === 'Escape' && !busy) onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [busy, onClose]);

  function update(field, value) {
    setForm((current) => ({ ...current, [field]: value }));
  }

  function chooseLogo(event) {
    const [file] = Array.from(event.target.files ?? []);
    event.target.value = '';
    if (!file) return;
    const svg = file.type === COMPANY_LOGO_TYPE || file.name.toLowerCase().endsWith('.svg');
    if (!svg || file.size < 32 || file.size > MAX_COMPANY_LOGO_BYTES) {
      setError(tx('ui.companies.invalid_logo'));
      return;
    }
    setError('');
    setLogoFile(file);
  }

  async function submit(event) {
    event.preventDefault();
    if (!editing && logoFile === null) {
      setError(tx('ui.companies.logo_required'));
      return;
    }
    setBusy(true);
    setError('');
    try {
      const payload = {
        code: form.code,
        name: form.name,
        websiteUrl: form.websiteUrl,
        address: form.address,
        email: form.email,
        phone: form.phone,
      };
      if (logoFile !== null) {
        const declaration = {
          contentType: COMPANY_LOGO_TYPE,
          byteSize: logoFile.size,
          sha256: await sha256File(logoFile),
        };
        const prepared = await prepareCompanyLogoUpload(declaration, company?.id ?? null);
        if (!prepared.alreadyUploaded) await putCompanyLogo(prepared.putUrl, logoFile);
        payload.logo = {
          ...declaration,
          companyUuid: prepared.companyUuid,
          objectKey: prepared.objectKey,
        };
        if (!editing) payload.companyUuid = prepared.companyUuid;
      }
      const saved = editing
        ? await updateCompany(company.id, payload)
        : await createCompany(payload);
      onSaved(saved);
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="xo-company-modal-backdrop" role="presentation"
      onMouseDown={() => { if (!busy) onClose(); }}>
      <form className="xo-company-modal xo-company-editor" onSubmit={submit}
        onMouseDown={(event) => event.stopPropagation()}>
        <header>
          <span className="xo-company-modal-symbol"><Building2 aria-hidden="true" /></span>
          <div>
            <p className="xo-eyebrow">{tx('ui.reports.company_7a199499')}</p>
            <h2>{editing ? tx('ui.companies.edit_company') : tx('ui.companies.create_company')}</h2>
            <span>{tx('ui.companies.all_fields_required')}</span>
          </div>
          <button type="button" className="xo-icon-btn" aria-label={tx('ui.parts.close')}
            disabled={busy} onClick={onClose}><X aria-hidden="true" /></button>
        </header>

        <div className="xo-company-editor-layout">
          <div className="xo-company-fields">
            <label><span>{tx('ui.kits.code_adac6937')}</span><input required maxLength="64"
              value={form.code} onChange={(event) => update('code', event.target.value)} /></label>
            <label><span>{tx('ui.reports.name_709a2322')}</span><input required maxLength="128"
              value={form.name} onChange={(event) => update('name', event.target.value)} /></label>
            <label><span>{tx('ui.companies.website')}</span><input required type="url" maxLength="2048"
              value={form.websiteUrl} onChange={(event) => update('websiteUrl', event.target.value)} /></label>
            <label><span>{tx('ui.cluster.fact_address')}</span><textarea required maxLength="1024"
              value={form.address} onChange={(event) => update('address', event.target.value)} /></label>
            <label><span>{tx('ui.notifications.email_84add5b2')}</span><input required type="email" maxLength="320"
              value={form.email} onChange={(event) => update('email', event.target.value)} /></label>
            <label><span>{tx('ui.profile.phone_number_8961d3bf')}</span><input required type="tel" maxLength="64"
              value={form.phone} onChange={(event) => update('phone', event.target.value)} /></label>
          </div>

          <div className="xo-company-logo-editor">
            <CompanyLogoArtwork logoUrl={logoPreview} name={form.name} />
            <label className="xo-company-logo-upload">
              <input type="file" accept="image/svg+xml,.svg" disabled={busy} onChange={chooseLogo} />
              <UploadCloud aria-hidden="true" />
              <span>{logoFile
                ? logoFile.name
                : editing ? tx('ui.companies.replace_logo') : tx('ui.companies.choose_logo')}</span>
            </label>
            <small>{tx('ui.companies.logo_requirements')}</small>
          </div>
        </div>

        {error && <p className="xo-alert t-bad" role="alert">{error}</p>}
        <footer>
          <button type="button" className="xo-btn-2" disabled={busy} onClick={onClose}>{tx('ui.parts.close')}</button>
          <button type="submit" className="xo-btn" disabled={busy}>
            {busy ? tx('ui.products.uploading_d921a79a') : (
              <><Check aria-hidden="true" />{editing ? tx('ui.reports.save_changes_179359b3') : tx('ui.companies.create_company')}</>
            )}
          </button>
        </footer>
      </form>
    </div>
  );
}

function CompanyCatalogue({ reportTypes, viewMode }) {
  if (reportTypes.length === 0) {
    return (
      <Empty
        title={tx('ui.content.no_content_types_match_cca12bca')}
        detail={tx('ui.staff.try_a_different_search_or_filter_4e653834')}
      />
    );
  }
  if (viewMode === 'grid') {
    return (
      <div className="xo-company-catalogue-grid">
        {reportTypes.map((reportType) => (
          <article className="xo-company-catalogue-card" key={reportType.id}>
            <header>
              <span>{reportType.displayName.slice(0, 1).toUpperCase()}</span>
              <div><strong>{reportType.displayName}</strong><small>{reportType.code}</small></div>
              <Pill tone={reportType.pipeline?.status === 'active' ? 'ok' : 'flat'}>
                {statusLabel(reportType.pipeline?.status)}
              </Pill>
            </header>
            <dl>
              <div><dt>{tx('ui.content.pipeline_32b1d5a7')}</dt><dd>{reportType.pipeline?.displayName ?? tx('ui.reports.pipeline_unavailable')}</dd></div>
            </dl>
          </article>
        ))}
      </div>
    );
  }
  return (
    <div className="xo-company-table-wrap">
      <table className="xo-table">
        <thead>
          <tr>
            <th>{tx('ui.reports.report_type_b68b8e02')}</th>
            <th>{tx('ui.kits.code_adac6937')}</th>
            <th>{tx('ui.content.pipeline_32b1d5a7')}</th>
            <th>{tx('ui.staff.state_a7250206')}</th>
          </tr>
        </thead>
        <tbody>
          {reportTypes.map((reportType) => (
            <tr key={reportType.id}>
              <td><strong>{reportType.displayName}</strong></td>
              <td className="xo-mono">{reportType.code}</td>
              <td>
                {reportType.pipeline === null ? (
                  <span className="xo-muted">{tx('ui.reports.pipeline_unavailable')}</span>
                ) : (
                  <span className="xo-lines">
                    <strong>{reportType.pipeline.displayName}</strong>
                    <span>{reportType.pipeline.codeVersion}</span>
                  </span>
                )}
              </td>
              <td>
                <Pill tone={reportType.pipeline?.status === 'active' ? 'ok' : 'flat'}>
                  {statusLabel(reportType.pipeline?.status)}
                </Pill>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function XomicsCompanies({ view = 'directory' }) {
  const navigate = useNavigate();
  const { permissions } = useXomicsAuth();
  const mayManage = permissions.includes('COMPANY_MANAGE');
  const [query, setQuery] = useState('');
  const [viewMode, setViewMode] = useState(view === 'directory' ? 'grid' : 'list');
  const [catalogueFilter, setCatalogueFilter] = useState('all');
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);
  const [editor, setEditor] = useState(null);
  const load = useCallback(() => loadCompanyCatalogue(), []);
  const catalogue = useLiveResource(['reports'], load);
  const loadDetail = useCallback(
    () => loadCompanyDetail(selectedCompanyId),
    [selectedCompanyId],
  );
  const detail = useLiveResource(['reports'], loadDetail, {
    enabled: selectedCompanyId !== null,
    reconcileMs: 0,
  });
  const data = catalogue.data ?? { companies: [], reportTypes: [], languages: [] };

  const companies = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (needle === '') return data.companies;
    return data.companies.filter((company) => (
      `${company.name} ${company.code}`.toLowerCase().includes(needle)
    ));
  }, [data.companies, query]);

  const reportTypes = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return data.reportTypes.filter((reportType) => {
      if (catalogueFilter === 'active' && reportType.pipeline?.status !== 'active') return false;
      if (catalogueFilter === 'unavailable' && reportType.pipeline !== null) return false;
      if (needle === '') return true;
      return `${reportType.displayName} ${reportType.code} ${reportType.pipeline?.displayName ?? ''} ${reportType.pipeline?.codeVersion ?? ''}`
        .toLowerCase().includes(needle);
    });
  }, [catalogueFilter, data.reportTypes, query]);

  function changeView(next) {
    setQuery('');
    setCatalogueFilter('all');
    setViewMode(next === 'directory' ? 'grid' : 'list');
    navigate(`/xomics/companies/${next}`);
  }

  useEffect(() => {
    if (selectedCompanyId === null || editor !== null) return undefined;
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') setSelectedCompanyId(null);
    };
    document.addEventListener('keydown', closeOnEscape);
    return () => document.removeEventListener('keydown', closeOnEscape);
  }, [editor, selectedCompanyId]);

  function savedCompany(company) {
    setEditor(null);
    setSelectedCompanyId(company.id);
    catalogue.refresh();
    detail.refresh();
  }

  return (
    <div className="xo-companies-page">
      <Head title={tx('ui.nav.companies_9dba7ad7')} />

      <SummaryBar>
        <SummaryItem value={data.companies.length} label={tx('ui.nav.companies_9dba7ad7')} />
        <SummaryItem value={data.reportTypes.length} label={tx('ui.nav.catalogue_5a241023')}
          detail={tx('ui.reports.many_report_types_available', { count: data.reportTypes.length })} />
        <SummaryItem value={data.languages.length} label={tx('ui.content.languages_db07be18')} />
      </SummaryBar>

      <div className="xo-company-navigation">
        <div className="xo-product-toolbar">
          <SearchField className="xo-product-search"
            value={query}
            onChange={setQuery}
            placeholder={view === 'directory'
              ? tx('ui.nav.companies_9dba7ad7')
              : tx('ui.content.search_content_catalogue_2c469e31')}
          />
          {view === 'directory' && mayManage && (
            <button type="button" className="xo-btn" onClick={() => {
              setSelectedCompanyId(null);
              setEditor({ mode: 'create', company: null });
            }}>
              <Plus aria-hidden="true" />{tx('ui.companies.add_company')}
            </button>
          )}
        </div>
        <div className="xo-product-section-row">
          <WorkspaceTabs
            className="xo-company-tabs"
            value={view}
            onChange={changeView}
            label={tx('ui.nav.companies_9dba7ad7')}
            tabs={[
              { value: 'directory', label: tx('ui.nav.directory_4b892fe0'), count: data.companies.length, Icon: List },
              { value: 'catalogue', label: tx('ui.nav.catalogue_5a241023'), count: data.reportTypes.length, Icon: BookOpen },
            ]}
          />
          <div className="xo-product-toolbar-actions xo-company-toolbar-actions">
            {view === 'catalogue' && (
              <XomicsSelect value={catalogueFilter}
                onChange={(event) => setCatalogueFilter(event.target.value)}>
                <option value="all">{tx("ui.kits.all_6a720856")}</option>
                <option value="active">{tx("ui.staff.active_a733b809")}</option>
                <option value="unavailable">{tx('ui.reports.pipeline_unavailable')}</option>
              </XomicsSelect>
            )}
            <div className="xo-product-view-switch" role="group"
              aria-label={tx("ui.products.product_layout_2f81698d")}>
              <button type="button" className={viewMode === 'grid' ? 'is-active' : ''}
                aria-pressed={viewMode === 'grid'} onClick={() => setViewMode('grid')}>
                <LayoutGrid aria-hidden="true" /><span>{tx("ui.products.grid_701c483f")}</span>
              </button>
              <button type="button" className={viewMode === 'list' ? 'is-active' : ''}
                aria-pressed={viewMode === 'list'} onClick={() => setViewMode('list')}>
                <List aria-hidden="true" /><span>{tx("ui.products.list_a1fffaaa")}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <section className="xo-company-surface">
        {catalogue.loading && catalogue.data === null ? (
          <Loading label={tx('ui.content.reading_content_catalogue_bbc34aef')} />
        ) : catalogue.error ? (
          <Failed message={catalogue.error} onRetry={catalogue.refresh} />
        ) : view === 'directory' ? (
          <CompanyDirectory companies={companies} viewMode={viewMode}
            onOpen={setSelectedCompanyId} />
        ) : (
          <CompanyCatalogue reportTypes={reportTypes} viewMode={viewMode} />
        )}
      </section>

      {editor === null && selectedCompanyId !== null && (
        detail.data?.id === selectedCompanyId ? (
          <CompanyDetail company={detail.data} mayManage={mayManage}
            onClose={() => setSelectedCompanyId(null)}
            onEdit={(company) => setEditor({ mode: 'edit', company })} />
        ) : (
          <div className="xo-company-modal-backdrop" role="presentation"
            onMouseDown={() => setSelectedCompanyId(null)}>
            <section className="xo-company-modal is-loading" role="dialog" aria-modal="true"
              onMouseDown={(event) => event.stopPropagation()}>
              {detail.error
                ? <Failed message={detail.error} onRetry={detail.refresh} />
                : <Loading />}
            </section>
          </div>
        )
      )}

      {editor !== null && (
        <CompanyEditor company={editor.company}
          onClose={() => setEditor(null)} onSaved={savedCompany} />
      )}

    </div>
  );
}
